'use client';

import { useCallback, useEffect, useState } from 'react';
import { AlertCircle, FileText, Upload, X } from 'lucide-react';
import { useT } from '@/components/i18n-provider';
import { Spinner } from '@/components/ui/spinner';
import { cn, formatBytes } from '@/lib/utils';

/**
 * The Content section of an agent's settings: the files the agent can read,
 * with upload and removal.
 *
 * What it replaced (agent-details.tsx until 2026-09-27) never showed a file
 * and never showed a failure. The list was a hard-coded empty state, and the
 * upload posted every file to `/api/upload` with `type=agent-content`, a
 * route that only knows avatars and post media and answers 400 to a PDF; the
 * handler awaited the fetch, discarded the response and cleared the input,
 * so a person who uploaded a document saw the picker close and nothing else
 * (e2e report, handoff 2026-09-26).
 *
 * The two routes that exist are used as designed:
 *   - `GET /api/agents/<id>/content` lists what the agent can reach: every
 *     content row with a link to the agent's user id (owner and agent may
 *     call it).
 *   - Uploading is two steps, because the agent route's POST is for the
 *     agent itself: `POST /api/content/upload` makes the row under the
 *     person's own files (private), then `POST /api/content/<id>/grants`
 *     adds the agent as a viewer, which is exactly the link the listing
 *     reads. Removal revokes that same grant; the file stays in the
 *     person's files.
 *
 * Every step reports: a load failure gets a retry, and each file that could
 * not be uploaded or attached is named with the server's reason.
 */

export type AgentContentItem = {
  id: string;
  displayName: string;
  contentType: string;
  mimeType: string;
  size: number;
  createdAt: string;
};

/** The document types the agent can read (`extractTextContent`); the picker's filter. */
export const AGENT_CONTENT_ACCEPT = '.pdf,.txt,.md,.csv,.json,.docx';

export function AgentContentSection({ agentId }: { agentId: string }) {
  const t = useT();
  // `null` until the first list answer: the loading state, distinct from an
  // empty list.
  const [items, setItems] = useState<AgentContentItem[] | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [notices, setNotices] = useState<string[]>([]);

  const refresh = useCallback(async () => {
    setLoadError(false);
    try {
      const res = await fetch(`/api/agents/${encodeURIComponent(agentId)}/content?limit=100`, {
        cache: 'no-store',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as { items?: AgentContentItem[] };
      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setLoadError(true);
    }
  }, [agentId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const upload = async (files: File[]) => {
    setUploading(true);
    setNotices([]);
    const failures: string[] = [];
    for (const file of files) {
      const form = new FormData();
      form.set('file', file);
      form.set('visibility', 'private');
      let contentId: string | null = null;
      try {
        const res = await fetch('/api/content/upload', { method: 'POST', body: form });
        const body = (await res.json().catch(() => ({}))) as { id?: string; error?: string };
        if (!res.ok || !body.id) {
          failures.push(
            t('agents.contentUploadFailed', { name: file.name, reason: body.error || `HTTP ${res.status}` }),
          );
          continue;
        }
        contentId = body.id;
      } catch {
        failures.push(t('agents.contentUploadFailed', { name: file.name, reason: t('create.networkError') }));
        continue;
      }
      try {
        const grant = await fetch(`/api/content/${encodeURIComponent(contentId)}/grants`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ grants: { add: [{ userId: agentId, role: 'viewer' }] } }),
        });
        if (!grant.ok) failures.push(t('agents.contentGrantFailed', { name: file.name }));
      } catch {
        failures.push(t('agents.contentGrantFailed', { name: file.name }));
      }
    }
    setNotices(failures);
    setUploading(false);
    await refresh();
  };

  const remove = async (item: AgentContentItem) => {
    setRemoving(item.id);
    setNotices([]);
    try {
      const res = await fetch(`/api/content/${encodeURIComponent(item.id)}/grants`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ grants: { remove: [{ userId: agentId }] } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setItems((prev) => (prev ?? []).filter((i) => i.id !== item.id));
    } catch {
      setNotices([t('agents.contentRemoveFailed', { name: item.displayName })]);
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm text-muted-foreground">{t('agents.contentDesc')}</p>
        <label
          className={cn(
            'btn-secondary text-xs flex items-center gap-1.5 cursor-pointer',
            uploading && 'opacity-60 pointer-events-none',
          )}
        >
          {uploading ? <Spinner size="sm" /> : <Upload className="h-3.5 w-3.5" />}
          {uploading ? t('agents.uploading') : t('agents.upload')}
          <input
            type="file"
            className="hidden"
            multiple
            accept={AGENT_CONTENT_ACCEPT}
            disabled={uploading}
            data-testid="agent-content-file-input"
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              e.target.value = '';
              if (files.length) void upload(files);
            }}
          />
        </label>
      </div>

      {notices.map((notice, i) => (
        <p key={i} role="alert" className="flex items-start gap-1 text-xs text-red-500">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" /> {notice}
        </p>
      ))}

      {loadError ? (
        <p role="alert" className="flex items-center gap-2 text-xs text-red-500">
          <AlertCircle className="h-3 w-3 shrink-0" /> {t('agents.contentLoadFailed')}
          <button type="button" onClick={() => void refresh()} className="underline hover:text-foreground">
            {t('agents.contentRetry')}
          </button>
        </p>
      ) : items === null ? (
        <div className="flex justify-center py-4" role="status">
          <Spinner size="sm" tone="muted" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-6 @md/main:p-8 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">{t('agents.noContent')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('agents.noContentDesc')}</p>
        </div>
      ) : (
        <ul className="divide-y divide-border rounded-lg border border-border" data-testid="agent-content-list">
          {items.map((item) => (
            <li key={item.id} className="flex items-center gap-3 px-3 py-2">
              <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <a
                  href={`/api/content/${encodeURIComponent(item.id)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block truncate text-sm hover:underline"
                >
                  {item.displayName}
                </a>
                <p className="text-[11px] text-muted-foreground">
                  {formatBytes(item.size)} · {new Date(item.createdAt).toLocaleDateString()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => void remove(item)}
                disabled={removing === item.id}
                aria-label={t('agents.contentRemove')}
                title={t('agents.contentRemove')}
                className="rounded-md p-1 text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-50"
              >
                {removing === item.id ? <Spinner size="sm" /> : <X className="h-3.5 w-3.5" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
